import { zodResolver } from '@hookform/resolvers/zod'
import { raise } from '@sparkdotfi/common-universal'
import { createElement, useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'

import { WithdrawValidationError } from '../components/WithdrawValidationError'
import { Mode, SavingsWithdrawTxOverview, SendModeExtension } from '../types'
import { createObjectives } from './createObjectives'
import { createTxOverview } from './createTxOverview'
import { useFormFieldsForWithdrawDialog } from './form/useFormFieldsForWithdrawDialog'
import { useSendModeExtension } from './form/useSendModeExtension'
import { useWithdrawFromSavingsValidator } from './form/useWithdrawFromSavingsValidator'
import { useShouldRedeem } from './useShouldRedeem'
import { getChainConfigEntry } from '@/config/chain'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useSimulationTimestamp } from '@/domain/hooks/useSimulationTimestamp'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { useDebouncedFormValues } from '@/features/dialogs/common/logic/transfer-from-user/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { SavingsTokenRepository, useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'
import { getAccountDisplayedUnderlyingToken } from '@/features/savings/utils/getAccountDisplayedUnderlyingToken'
import { AssetInputRenderError } from '@/ui/organisms/asset-input/AssetInput'

export interface UseSavingsWithdrawDialogParams {
  mode: Mode
  savingsToken: Token
  chainId: number
}

export interface UseSavingsWithdrawDialogResults {
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  objectives: Objective[]
  tokenToWithdraw: TokenWithValue
  pageStatus: PageStatus
  txOverview: SavingsWithdrawTxOverview
  underlyingToken: Token
  accountName: string
  sendModeExtension?: SendModeExtension
  renderWithdrawValidationError: AssetInputRenderError
  actionsViewState: ActionsViewState
}

export function useSavingsWithdrawDialog({
  mode,
  chainId,
  savingsToken,
}: UseSavingsWithdrawDialogParams): UseSavingsWithdrawDialogResults {
  const { tenderlyClient } = useSandbox()
  const { simulationTimestamp, refresh, isFetching } = useSimulationTimestamp({ chainId })

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    void (async () => {
      if (tenderlyClient) {
        await tenderlyClient.mineBlocks(1n)
      }
      refresh()
    })()
  }, [chainId, tenderlyClient])

  const savingsAccounts = useSavingsAccountRepository({ chainId, timestamp: simulationTimestamp })

  const chainConfig = getChainConfigEntry(chainId)
  const savingsConfig = chainConfig.savings ?? raise('Savings config not available for this chain')

  const { account } = useConnectedAddress()
  const tokenRepository = useSavingsTokenRepository({ chainId, account })

  const selectedAccountConfig =
    savingsConfig.accounts.find((account) => account.savingsToken === savingsToken.symbol) ??
    raise('Savings account is not found')
  const selectedAccount = savingsAccounts.findOneBySavingsToken(savingsToken)
  const supportedAssets = selectedAccountConfig.supportedAssets.map((symbol) => tokenRepository.findOneBySymbol(symbol))
  const [pageState, setPageState] = useState<PageState>('form')
  const sendModeExtension = useSendModeExtension({ mode, tokenRepository })
  // @todo: useSnapshot used for success screen
  const [savingsTokenSnapshot, refreshSavingsTokenSnapshot] = useSnapshot(
    tokenRepository.findOneBySymbol(savingsToken.symbol),
  )
  const [savingsTokenBalanceSnapshot, refreshSavingsTokenBalanceSnapshot] = useSnapshot(
    tokenRepository.findOneBalance(savingsToken.symbol),
  )

  const displayedUnderlyingToken = getAccountDisplayedUnderlyingToken({
    account: selectedAccount,
    chainId,
  })

  const accountName =
    savingsToken.symbol === TokenSymbol('stUSDS') ? 'Sky stUSDS' : `${displayedUnderlyingToken.symbol} Savings`

  const validator = useWithdrawFromSavingsValidator({
    chainId,
    timestamp: simulationTimestamp,
    tokenRepository,
    savingsToken: savingsTokenSnapshot,
    underlyingToken: selectedAccount.underlyingToken,
    savingsTokenBalance: savingsTokenBalanceSnapshot,
    savingsConverter: selectedAccount.converter,
  })
  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(validator),
    defaultValues: {
      symbol: displayedUnderlyingToken.symbol,
      value: '',
      isMaxSelected: false,
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const { shouldRedeem, onMaxSet } = useShouldRedeem({
    converter: selectedAccount.converter,
    savingsTokenBalance: savingsTokenBalanceSnapshot,
    tokenRepository,
    form,
  })

  const objectives = createObjectives({
    formValues,
    receiver: sendModeExtension?.receiver,
    mode,
    savingsToken: savingsTokenSnapshot,
    savingsTokenBalance: savingsTokenBalanceSnapshot,
    shouldRedeem,
  })
  const txOverview = createTxOverview({
    formValues,
    sendModeExtension,
    savingsAccount: selectedAccount,
    savingsTokenBalance: savingsTokenBalanceSnapshot,
    shouldRedeem,
  })

  const assetsFields = useFormFieldsForWithdrawDialog({
    form,
    tokenRepository,
    savingsAccount: selectedAccount,
    savingsTokenBalance: savingsTokenBalanceSnapshot,
    onMaxSet,
    timestamp: simulationTimestamp,
    chainId,
  })

  // @todo: useSnapshot used for success screen
  const [tokenToWithdraw, refreshTokenToWithdraw] = useSnapshot({
    token: formValues.token,
    value: txOverview.status === 'success' ? txOverview.tokenToReceiveAmount : formValues.value, // @note should be deprecated in favor or extendedWithdrawTxOutcome
  })

  const actionsEnabled =
    ((formValues.value.gt(0) && isFormValid) || formValues.isMaxSelected) &&
    !isDebouncing &&
    (sendModeExtension?.enableActions ?? true) &&
    !isFetching

  const underlyingToken = tokenRepository.findOneBySymbol(selectedAccount.underlyingToken.symbol)

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      savingsAccounts,
      chainId,
    },
    onFinish: () => setPageState('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
    refreshSavingsTokenSnapshot()
    refreshSavingsTokenBalanceSnapshot()
    refreshTokenToWithdraw()
  }, [
    formValues.token.symbol,
    formValues.value.toString(),
    formValues.isMaxSelected,
    sendModeExtension?.receiver,
    resetActions,
    refreshSavingsTokenSnapshot,
    refreshSavingsTokenBalanceSnapshot,
    refreshTokenToWithdraw,
  ])

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function renderWithdrawValidationError({ error, dataTestId }: { error?: string; dataTestId?: string }) {
    return createElement(WithdrawValidationError, { message: error, 'data-testid': dataTestId, chainId })
  }

  return {
    selectableAssets: filterInputTokens({
      inputTokens: supportedAssets,
      savingsToken,
      tokenRepository,
      sendMode: !!sendModeExtension,
    }).map((token) => ({
      token,
      balance: tokenRepository.findOneBalanceOrZero(token.symbol),
    })),
    assetsFields,
    form,
    objectives,
    tokenToWithdraw,
    pageStatus: {
      state: pageState,
      actionsEnabled,
      goToSuccessScreen: () => setPageState('success'),
    },
    accountName,
    txOverview,
    sendModeExtension,
    underlyingToken,
    renderWithdrawValidationError,
    actionsViewState,
  }
}

interface FilterInputTokensParams {
  inputTokens: Token[]
  savingsToken: Token
  tokenRepository: SavingsTokenRepository
  sendMode: boolean
}
function filterInputTokens({ inputTokens, savingsToken, tokenRepository, sendMode }: FilterInputTokensParams): Token[] {
  if (savingsToken.symbol === TokenSymbol('sDAI')) {
    return inputTokens
  }

  const usdsSymbol = tokenRepository.findBySymbol(TokenSymbol('USDS'))?.symbol

  // Send mode is disabled for ETH due to necessity to unwrap from WETH to ETH first
  const tokens = sendMode
    ? [usdsSymbol, TokenSymbol('USDT'), TokenSymbol('USDC'), TokenSymbol('WETH')]
    : [usdsSymbol, TokenSymbol('USDT'), TokenSymbol('USDC'), TokenSymbol('WETH'), TokenSymbol('ETH')]

  return inputTokens.filter((token) => tokens.includes(token.symbol))
}
